import React, { type ReactElement, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import * as yup from 'yup';
import * as _ from 'lodash';
import { CommonTextField, CommonButtonV1 } from 'gemlux-common-components';
import RedCloseCircleSVG from '../../assets/img/close-circle-bold-duotone.svg';
import { theme } from '../../theme/theme';
import { authActions } from '../../redux/auth/authSlice';
import { RootState } from '../../redux/store';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { LOGIN_ROUTE } from '../../utils/routes';
import VisibilitySvg from '../../assets/img/visibility.svg';
import VisibilityOffSvg from '../../assets/img/visibility-off.svg';
import { validation } from '../../schema/validation';
import LoaderSvg from '../../assets/img/dot-revolve.svg';
import CustomContainerBox from '../../components/V1/CustomContainerBox/CustomContainerBox';
import LogoIcon from '../../assets/img/logo-large.svg';
import GreenCheckCircleSVG from '../../assets/img/green-check-frame.svg';

export default function ResetPassword(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [resetToken, setResetToken] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isNewUser = Boolean(new URLSearchParams(location.search).get('newUser'));

  const componentStatus = useAppSelector((state: RootState) => state.auth.componentStatus);
  const isLoading = _.get(componentStatus, 'isLoading', true) as boolean;
  const isInvalidToken = _.get(componentStatus, 'isInvalidToken', false) as string;
  const role = _.get(componentStatus, 'role', '');
  const error = _.get(componentStatus, 'error', '') as string;
  const isSubmitting = _.get(componentStatus, 'isSubmitting', false) as boolean;

  const handleGoBackToLogin = () => {
    navigate(LOGIN_ROUTE, { replace: true });
  };

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('token') as string;
    if (!resetToken && token) {
      setResetToken(token);
      dispatch(authActions.validatePasswordResetTokenAsync({ token }));
    } else {
      navigate(LOGIN_ROUTE, { replace: true });
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: yup.object({
      newPassword: validation.password,
      confirmPassword: yup
        .string()
        .oneOf([yup.ref('newPassword')], 'Password doesn’t match!')
        .required('Confirm password is required!'),
    }),
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: ({ newPassword }) => {
      dispatch(authActions.setComponentStatus({ isSubmitting: true, isInvalidToken: false, role: '', error: '' }));
      dispatch(authActions.updatePasswordAsync({ token: resetToken, newPassword, isNewUser }));
    },
  });

  const enterKeyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      formik.handleSubmit();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', enterKeyDownHandler);
    return () => {
      document.removeEventListener('keydown', enterKeyDownHandler);
    };
  }, []);

  if (isLoading) {
    return <Box component="img" src={LoaderSvg} />;
  }
  if (isInvalidToken) {
    return (
      <CustomContainerBox>
        <Box
          component="img"
          width="56px"
          display="flex"
          margin="auto"
          src={RedCloseCircleSVG}
          alt="red-close-circle-icon"
        />
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="title3Bold" textAlign="center">
            The link is expired!
          </Typography>
          <Typography variant="bodyRegular" textAlign="center" color="text.secondary">
            Your password reset link is expired or invalid.
          </Typography>
        </Box>
      </CustomContainerBox>
    );
  }
  if (role) {
    return (
      <CustomContainerBox>
        <Box component="img" display="flex" margin="auto" src={LogoIcon} alt="green-check-circle-icon" />
        <Box
          component="img"
          display="flex"
          margin="auto"
          src={GreenCheckCircleSVG}
          alt="green-check-circle-icon"
          mt="56px"
        />

        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="textH5" textAlign="center">
            Password Updated!
          </Typography>
          <Typography variant="textBase" textAlign="center" color="text.secondary">
            Your password has been updated. Please go to the sign-in page and use your new password to log in.
          </Typography>
        </Box>

        <Box display="flex" width="100%" my="28px">
          <CommonButtonV1 fullWidth backgroundColor="#00A81C" onClick={handleGoBackToLogin}>
            <Typography color="HighlightText">Go to Sign In Page</Typography>
          </CommonButtonV1>
        </Box>
      </CustomContainerBox>
    );
  }
  const passwordFieldType = showPassword ? 'text' : 'password';
  const confirmPasswordFieldType = showConfirmPassword ? 'text' : 'password';

  return (
    <CustomContainerBox>
      <Box component="img" display="flex" margin="auto" src={LogoIcon} alt="green-check-circle-icon" my="56px" />
      <Typography variant="textH3" textAlign="center">
        Reset Password
      </Typography>
      <Box display="flex" flexDirection="column" justifyContent="space-around" alignItems="center" width="100%">
        <Box width="100%" gap={2} display="flex" flexDirection="column">
          <Box display="flex" flexDirection="column" width="100%">
            <Typography mb={0.5} variant="textBase500" component="p">
              New Password
            </Typography>
            <CommonTextField
              type={passwordFieldType}
              name="newPassword"
              fullWidth
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
              helperText={formik.touched.newPassword && formik.errors.newPassword}
              variant="outlined"
              FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
              placeholder="Password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseLeave={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                      sx={{
                        backgroundColor: '#E0EDFF',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '8px',
                        padding: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          backgroundColor: theme.palette.secondary.light,
                        },
                      }}
                    >
                      {showPassword ? (
                        <Box component="img" src={VisibilityOffSvg} />
                      ) : (
                        <Box component="img" src={VisibilitySvg} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography mb={0.5} variant="textBase500" component="p">
              Confirm Password
            </Typography>
            <CommonTextField
              name="confirmPassword"
              fullWidth
              type={confirmPasswordFieldType}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              variant="outlined"
              FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
              placeholder="Password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      onMouseLeave={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                      sx={{
                        backgroundColor: '#E0EDFF',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '8px',
                        padding: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          backgroundColor: theme.palette.secondary.light,
                        },
                      }}
                    >
                      {showConfirmPassword ? (
                        <Box component="img" src={VisibilityOffSvg} />
                      ) : (
                        <Box component="img" src={VisibilitySvg} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </Box>
      <CommonButtonV1
        loading={isSubmitting}
        variant="contained"
        color="primary"
        fullWidth
        onClick={() => formik.handleSubmit()}
      >
        Update Password
      </CommonButtonV1>
      <Typography hidden={!error} variant="footnoteBold" component="p" textAlign="center" color="error">
        {error}
      </Typography>
    </CustomContainerBox>
  );
}
